import { createContext, ReactElement, useEffect, useState } from "react";
import {
  IBasicEntity,
  ICategory,
  ICategoryQMOS,
  IDelayConfigQMOS,
  ISelectedNode,
  IState,
  SchemaNodeType
} from "../types";
import { useSelector } from "react-redux";
import { ICategoryProcessed, processQMOSTreeNodes, processTreeNodes } from "../modules/interruptions/utils";
import { useLazyFetch } from "../system/useFetch";

interface IDelayContextData {
  qmos: boolean;
  categories: ICategory[] | ICategoryQMOS[];
  consolidated: ICategoryProcessed | null;
  responsibility?: IBasicEntity[];
}

type IDelayProviderProps = {
  children: ReactElement[] | ReactElement;
};

const emptyDelayContextData: IDelayContextData = {
  qmos: false,
  categories: [],
  consolidated: null
};

const DelayContext = createContext<IDelayContextData>(emptyDelayContextData);
const { Provider } = DelayContext;

const DelayContextProvider = ({ children }: IDelayProviderProps): ReactElement => {
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [ executeQuery, { data } ] = useLazyFetch<ICategory[] | IDelayConfigQMOS>(true);
  const [categoryProcessed, setCategoryProcessed] = useState<IDelayContextData | null>(null);

  useEffect(() => {
    if (selectedNode.type !== SchemaNodeType.None) {
      executeQuery ({
        module: "delay",
        query: "getCategoriesByAreaID",
        payload: { nodeID: selectedNode.nodeID, nodeType: selectedNode.type }
      });
    }

  }, [selectedNode]);

  useEffect(() => {
    if (data) {
      const qmos = "relationship" in data;

      if (qmos) {
        setCategoryProcessed({
          qmos,
          categories: processQMOSTreeNodes(data as IDelayConfigQMOS),
          consolidated: null,
          responsibility: (data as IDelayConfigQMOS).responsibility
        });
      } else {
        setCategoryProcessed({
          qmos,
          categories: data as ICategory[],
          consolidated: processTreeNodes(data as ICategory[])
        });
      }
    }
  }, [data]);


  return (
    <Provider value={categoryProcessed ?? emptyDelayContextData}>
      {children}
    </Provider>
  );
};

export { DelayContextProvider, DelayContext };
